"use client";

import { motion } from "framer-motion";
import { itemVariants } from "@/lib/animation-variants";

interface PageHeaderProps {
  title: string | React.ReactNode;
  subtitle?: string;
  level?: "h1" | "h2";
  centered?: boolean;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
}

export function PageHeader({
  title,
  subtitle,
  level = "h1",
  centered = true,
  className = "",
  titleClassName = "",
  subtitleClassName = "",
}: PageHeaderProps) {
  const HeadingTag = level;

  const baseTitleClasses = `${level === "h1" ? "text-headline" : "text-section-title"} ${centered ? "text-center" : ""} mb-4 text-gothia-gold-dark`;
  const baseSubtitleClasses = `text-intro text-gray-200 ${centered ? "text-center" : ""}`;

  const titleClasses = `${baseTitleClasses} ${titleClassName}`;
  const subtitleClasses = `${baseSubtitleClasses} ${subtitleClassName}`;

  return (
    <motion.div
      variants={itemVariants}
      className={`${centered ? "text-center" : ""} ${className}`}>
      <HeadingTag className={titleClasses}>{title}</HeadingTag>

      {subtitle && (
        <motion.p variants={itemVariants} className={subtitleClasses}>
          {subtitle}
        </motion.p>
      )}
    </motion.div>
  );
}

// Specialized variants for common use cases
export function SectionHeader({
  title,
  subtitle,
  className = "",
}: Omit<PageHeaderProps, "level" | "centered">) {
  return (
    <PageHeader
      title={title}
      subtitle={subtitle}
      level="h2"
      centered={true}
      className={`mb-16 ${className}`}
    />
  );
}

export function PageTitle({
  title,
  subtitle,
  className = "",
}: Omit<PageHeaderProps, "level" | "centered">) {
  return (
    <PageHeader
      title={title}
      subtitle={subtitle}
      level="h1"
      centered={true}
      className={`mb-8 ${className}`}
    />
  );
}
